import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createMercadoPagoQrForCustomerPayment,
  getCustomerPaymentQrStatus,
  mpQrCountdownSeconds,
  mpQrPollIntervalMs,
  type MpQrPaymentPhase,
} from '../../../mercadopago/public-api';
import { captureSentryError } from '../../../shared/sentry/sentryUser';

export type CustomerPaymentQrSessionState = {
  phase: MpQrPaymentPhase;
  loading: boolean;
  error: string | null;
  qrData: string | null;
  amount: number | null;
  expiresAt: string | null;
  countdownSeconds: number | null;
  movementId: string | null;
};

const IDLE_STATE: CustomerPaymentQrSessionState = {
  phase: 'idle',
  loading: false,
  error: null,
  qrData: null,
  amount: null,
  expiresAt: null,
  countdownSeconds: null,
  movementId: null,
};

const MAX_TRACKING_MS = 16 * 60 * 1000;

function intentStatusToPhase(status: string, hasQr: boolean, expiresAt: string | null): MpQrPaymentPhase {
  switch (status) {
    case 'approved':
      return 'approved';
    case 'expired':
      return 'expired';
    case 'failed':
      return 'rejected';
    // MP confirmó pero el cobro no aplicó (ej. la deuda se cubrió por otro
    // medio): la plata quedó en MP. No es un "rechazo" ni se regenera el QR.
    case 'settle_failed':
      return 'error';
    case 'cancelled':
      return 'voided';
    default:
      break;
  }
  if (expiresAt) {
    const expiresMs = new Date(expiresAt).getTime();
    if (Number.isFinite(expiresMs) && expiresMs <= Date.now()) return 'expired';
  }
  return hasQr ? 'waiting_scan' : 'generating';
}

/**
 * Sesión QR de un cobro de cuenta corriente: genera la orden MP para el intent
 * y pollea `get-customer-payment-qr` (que sincroniza con MP como fallback del
 * webhook) hasta que el intent queda terminal.
 */
export function useCustomerPaymentQrSession({
  intentId,
  onApproved,
}: {
  intentId: string | null;
  onApproved?: (movementId: string | null) => void;
}) {
  const [state, setState] = useState<CustomerPaymentQrSessionState>(IDLE_STATE);
  const startedAtRef = useRef<number>(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const approvedRef = useRef(false);
  const settleFailedReportedRef = useRef(false);
  const onApprovedRef = useRef(onApproved);
  onApprovedRef.current = onApproved;

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const applyStatus = useCallback((raw: {
    status: string;
    qrData: string | null;
    amount: number | null;
    expiresAt: string | null;
    movementId: string | null;
    settleError?: string | null;
  }) => {
    const hasQr = Boolean(raw.qrData);
    const phase = intentStatusToPhase(raw.status, hasQr, raw.expiresAt);
    setState({
      phase,
      loading: false,
      error: raw.status === 'settle_failed'
        ? 'Mercado Pago confirmó el pago pero el cobro no se pudo registrar (posiblemente la deuda ya se cubrió por otro medio). El dinero quedó en Mercado Pago — devolvelo desde tu cuenta de MP.'
        : null,
      qrData: raw.qrData,
      amount: raw.amount,
      expiresAt: raw.expiresAt,
      countdownSeconds: mpQrCountdownSeconds(raw.expiresAt),
      movementId: raw.movementId,
    });
    if (phase === 'approved' && !approvedRef.current) {
      approvedRef.current = true;
      onApprovedRef.current?.(raw.movementId);
    }
    if (raw.status === 'settle_failed' && !settleFailedReportedRef.current) {
      settleFailedReportedRef.current = true;
      captureSentryError(new Error(`Cobro QR de cuenta settle_failed: ${raw.settleError ?? 'sin detalle'}`), {
        tags: { module: 'customers', op: 'payment_qr.settle_failed' },
        extra: { amount: raw.amount, movementId: raw.movementId },
      });
    }
    return phase;
  }, []);

  const schedulePoll = useCallback(
    (currentIntentId: string) => {
      stopPolling();
      const elapsed = Date.now() - startedAtRef.current;
      if (elapsed > MAX_TRACKING_MS) return;
      pollTimerRef.current = setTimeout(async () => {
        try {
          const fresh = await getCustomerPaymentQrStatus(currentIntentId);
          const phase = applyStatus({
            status: fresh.status,
            qrData: fresh.qrData,
            amount: fresh.amount,
            expiresAt: fresh.expiresAt,
            movementId: fresh.movementId,
            settleError: fresh.settleError,
          });
          if (phase === 'waiting_scan' || phase === 'generating') {
            schedulePoll(currentIntentId);
          }
        } catch {
          // Error de red transitorio: seguir polleando mientras haya tiempo.
          schedulePoll(currentIntentId);
        }
      }, mpQrPollIntervalMs(elapsed));
    },
    [applyStatus, stopPolling]
  );

  const generate = useCallback(
    async (currentIntentId: string, regenerate = false) => {
      setState((prev) => ({ ...prev, phase: 'generating', loading: true, error: null }));
      try {
        const created = await createMercadoPagoQrForCustomerPayment(currentIntentId, { regenerate });
        setState((prev) => ({
          ...prev,
          phase: 'waiting_scan',
          loading: false,
          qrData: created.qrData,
          amount: created.amount ?? prev.amount,
          expiresAt: created.expiresAt,
          countdownSeconds: mpQrCountdownSeconds(created.expiresAt),
        }));
        schedulePoll(currentIntentId);
      } catch (error) {
        setState((prev) => ({
          ...prev,
          phase: 'error',
          loading: false,
          error: error instanceof Error ? error.message : 'No se pudo generar el QR.',
        }));
      }
    },
    [schedulePoll]
  );

  useEffect(() => {
    if (!intentId) {
      stopPolling();
      approvedRef.current = false;
      setState(IDLE_STATE);
      return;
    }
    startedAtRef.current = Date.now();
    approvedRef.current = false;
    settleFailedReportedRef.current = false;
    void generate(intentId);
    return () => stopPolling();
  }, [intentId, generate, stopPolling]);

  // Countdown tick mientras espera el escaneo.
  useEffect(() => {
    if (state.phase !== 'waiting_scan' || !state.expiresAt) return;
    const interval = setInterval(() => {
      const seconds = mpQrCountdownSeconds(state.expiresAt);
      setState((prev) =>
        prev.phase === 'waiting_scan' ? { ...prev, countdownSeconds: seconds } : prev
      );
      if (seconds != null && seconds <= 0) {
        setState((prev) => (prev.phase === 'waiting_scan' ? { ...prev, phase: 'expired' } : prev));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [state.phase, state.expiresAt]);

  const regenerate = useCallback(() => {
    if (!intentId) return;
    void generate(intentId, true);
  }, [intentId, generate]);

  const reset = useCallback(() => {
    stopPolling();
    approvedRef.current = false;
    setState(IDLE_STATE);
  }, [stopPolling]);

  return { state, regenerate, reset };
}
